import {
  useState, useCallback, useRef 
} from 'react';
import { streamGenAI } from '@/lib/genai-client';
import type {
  QueuedImage,
  AnalysisMode,
  InvoiceAnalysisResult,
  BoxCountResult,
  ProductVerificationResult,
  ConsolidatedReceivingReport,
  BoxCountItem,
  InvoiceItem,
} from '../types/ai-analysis';
import type { Delivery } from '../types';

type AnalysisResult = InvoiceAnalysisResult | BoxCountResult | ProductVerificationResult;

interface UseImageQueueOptions {
  delivery?: Delivery | null;
  onQueueComplete?: (report: ConsolidatedReceivingReport) => void;
}

interface UseImageQueueReturn {
  images: QueuedImage[];
  isProcessing: boolean;
  currentProcessingId: string | null;
  consolidatedReport: ConsolidatedReceivingReport | null;
  addImage: (base64: string, mediaType: 'image/jpeg' | 'image/png', mode: AnalysisMode) => void;
  removeImage: (id: string) => void;
  clearQueue: () => void;
  processQueue: () => Promise<void>;
  generateReport: () => ConsolidatedReceivingReport;
}

const PROMPT_KEYS: Record<AnalysisMode, string> = {
  invoice: 'invoice-analysis',
  'box-counting': 'box-counting',
  'product-verification': 'product-verification',
};

const USER_PROMPTS: Record<AnalysisMode, string> = {
  invoice: 'Please analyze this invoice/delivery document and extract all line items with their details.',
  'box-counting': 'Please count all boxes, pallets, and containers visible in this image and note any concerns.',
  'product-verification': 'Please read and extract all information from this product label including lot number, expiry date, and allergens.',
};

function generateId(): string {
  // Using crypto.randomUUID for secure unique ID generation
  return `img-${Date.now()}-${crypto.randomUUID().slice(0, 8)}`;
}

export function useImageQueue(options: UseImageQueueOptions = {}): UseImageQueueReturn {
  const {
    delivery,
    onQueueComplete,
  } = options;

  const [images, setImages] = useState<QueuedImage[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentProcessingId, setCurrentProcessingId] = useState<string | null>(null);
  const [consolidatedReport, setConsolidatedReport] = useState<ConsolidatedReceivingReport | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);

  const addImage = useCallback((
    base64: string,
    mediaType: 'image/jpeg' | 'image/png',
    mode: AnalysisMode
  ) => {
    const newImage: QueuedImage = {
      id: generateId(),
      base64,
      mediaType,
      mode,
      status: 'pending',
      result: null,
      error: null,
      timestamp: Date.now(),
    };
    setImages((prev) => [
      ...prev,
      newImage,
    ]);
  }, []);

  const removeImage = useCallback((id: string) => {
    setImages((prev) => prev.filter((img) => img.id !== id));
  }, []);

  const clearQueue = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setImages([]);
    setIsProcessing(false);
    setCurrentProcessingId(null);
    setConsolidatedReport(null);
  }, []);

  const processImage = useCallback(async (image: QueuedImage): Promise<AnalysisResult> => {
    const context: Record<string, unknown> = {};
    if (delivery) {
      context.poNumber = delivery.poNumber;
      context.supplierName = delivery.supplierName;
      context.expectedItems = delivery.lines.map((line) => ({
        itemName: line.itemName,
        sku: line.sku,
        orderedQuantity: line.orderedQuantity,
        unit: line.unit,
      }));
    }

    return new Promise((resolve, reject) => {
      streamGenAI<AnalysisResult>(
        {
          promptKey: PROMPT_KEYS[image.mode],
          userPrompt: USER_PROMPTS[image.mode],
          context,
          image: {
            base64: image.base64,
            mediaType: image.mediaType,
          },
        },
        () => {
          // onChunk - we don't need streaming text for queue processing
        },
        (result) => {
          resolve(result);
        },
        (error) => {
          reject(error);
        },
        abortControllerRef.current?.signal
      );
    });
  }, [delivery]);

  const processQueue = useCallback(async () => {
    const pendingImages = images.filter((img) => img.status === 'pending');
    if (pendingImages.length === 0) return;

    setIsProcessing(true);
    abortControllerRef.current = new AbortController();

    for (const image of pendingImages) {
      if (abortControllerRef.current.signal.aborted) break;

      setCurrentProcessingId(image.id);
      setImages((prev) =>
        prev.map((img) =>
          img.id === image.id ? {
            ...img,
            status: 'analyzing' as const,
          } : img
        )
      );

      try {
        const result = await processImage(image);
        setImages((prev) =>
          prev.map((img) =>
            img.id === image.id
              ? {
                ...img,
                status: 'complete' as const,
                result,
                error: null,
              }
              : img
          )
        );
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Analysis failed';
        setImages((prev) =>
          prev.map((img) =>
            img.id === image.id
              ? {
                ...img,
                status: 'error' as const,
                error: errorMessage,
              }
              : img
          )
        );
      }
    }

    setCurrentProcessingId(null);
    setIsProcessing(false);

    // Generate consolidated report after processing
    // Use setTimeout to ensure state is updated
    setTimeout(() => {
      setImages((currentImages) => {
        const report = generateReportFromImages(currentImages);
        setConsolidatedReport(report);
        onQueueComplete?.(report);
        return currentImages;
      });
    }, 0);
  }, [images, processImage, onQueueComplete]);

  const generateReportFromImages = (imageList: QueuedImage[]): ConsolidatedReceivingReport => {
    const completedImages = imageList.filter((img) => img.status === 'complete' && img.result);

    const boxCountResults = completedImages
      .filter((img) => img.mode === 'box-counting')
      .map((img) => img.result as BoxCountResult);

    const invoiceResults = completedImages
      .filter((img) => img.mode === 'invoice')
      .map((img) => img.result as InvoiceAnalysisResult);

    const productResults = completedImages
      .filter((img) => img.mode === 'product-verification')
      .map((img) => img.result as ProductVerificationResult);

    // Aggregate box counts
    const allCounts: BoxCountItem[] = boxCountResults.flatMap((r) => r.counts);
    const totalBoxes = boxCountResults.reduce((sum, r) => sum + r.totalBoxes, 0);
    const totalPallets = boxCountResults.reduce((sum, r) => sum + r.totalPallets, 0);
    const allConcerns = boxCountResults.flatMap((r) => r.concerns);
    const lastObservations = boxCountResults.length > 0 ? boxCountResults[boxCountResults.length - 1].observations : null;

    // Aggregate invoice items (dedupe by product name + SKU)
    const allInvoiceItems: InvoiceItem[] = [];
    const seenItems = new Set<string>();
    for (const result of invoiceResults) {
      for (const item of result.items) {
        const key = `${item.productName}-${item.sku ?? ''}`;
        if (!seenItems.has(key)) {
          seenItems.add(key);
          allInvoiceItems.push(item);
        }
      }
    }

    const invoiceNumbers = invoiceResults
      .map((r) => r.invoiceNumber)
      .filter((n): n is string => n !== null);

    const supplierNames = invoiceResults
      .map((r) => r.supplierName)
      .filter((n): n is string => n !== null);

    const totals = invoiceResults.reduce(
      (acc, r) => ({
        subtotal: acc.subtotal + (r.subtotal ?? 0),
        tax: acc.tax + (r.tax ?? 0),
        total: acc.total + (r.total ?? 0),
      }),
      {
        subtotal: 0,
        tax: 0,
        total: 0,
      }
    );

    return {
      totalImages: imageList.length,
      processedImages: completedImages.length,
      boxCounting: {
        totalBoxes,
        totalPallets,
        allCounts,
        concerns: [...new Set(allConcerns)],
        observations: lastObservations,
      },
      invoiceData: {
        items: allInvoiceItems,
        invoiceNumbers: [...new Set(invoiceNumbers)],
        supplierNames: [...new Set(supplierNames)],
        totals,
      },
      productVerifications: productResults,
      generatedAt: new Date().toISOString(),
    };
  };

  const generateReport = useCallback((): ConsolidatedReceivingReport => {
    return generateReportFromImages(images);
  }, [images]);

  return {
    images,
    isProcessing,
    currentProcessingId,
    consolidatedReport,
    addImage,
    removeImage,
    clearQueue,
    processQueue,
    generateReport,
  };
}
